import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { Empty, Select } from '@maxtropy/components';
import type { PromiseWithErrorHandler } from '@maxtropy/components';
import { Spin } from 'antd';
import { useRequest } from 'ahooks';
import classNames from 'classnames';
import titlePng from '@/assets/images/title_icon.png';
import SubTitle from '@/pages/WgqAllMaps/components/SubTitle';
import { ShareDataWrapperContext } from '@/pages/WgqAllMaps';
import RecorderPreview, { RecorderPreviewItem } from '@/components/RecorderPreview';
import buildingImg from '../../../../../../imgs/building.png';
import styles from './index.module.scss';

export enum RobotImageType {
  INFRARED = 'infrared',
  VISIBLE_LIGHT = 'visibleLight',
}

type ConfigItem = {
  id?: number;
  name?: string;
};

type CameraInfo = RecorderPreviewItem & {
  name?: string;
  accountId?: number;
  deviceSerial?: string;
  supervisoryUrl?: string;
  rtspAddress?: string;
  liveUrlTranscode?: string;
};

type RobotDetail = {
  id?: number;
  name?: string;
  infrared?: CameraInfo;
  visibleLight?: CameraInfo;
};

interface InspectionRobotPanelProps {
  title: string;
  icon: string;
  iconAlt: string;
  titleFlex?: string;
  listRequest: () => PromiseWithErrorHandler<{ list?: ConfigItem[] }>;
  detailRequest: (id: number) => PromiseWithErrorHandler<RobotDetail>;
}

const getCameraLabel = (type: RobotImageType) => (type === RobotImageType.INFRARED ? '红外光图' : '可见光图');

const InspectionRobotPanel: React.FC<InspectionRobotPanelProps> = ({
  title,
  icon,
  iconAlt,
  titleFlex = '1 1 30%',
  listRequest,
  detailRequest,
}) => {
  const { isStandardSize, loadMap } = useContext(ShareDataWrapperContext);
  const isLargeMapLayout = !isStandardSize && loadMap;
  const [selectedId, setSelectedId] = useState<number>();
  const [imageType, setImageType] = useState<RobotImageType>(RobotImageType.VISIBLE_LIGHT);

  useEffect(() => {
    setSelectedId(undefined);
    setImageType(RobotImageType.VISIBLE_LIGHT);
  }, [title]);

  const { data: listData, loading: listLoading } = useRequest(listRequest, {
    refreshDeps: [listRequest],
  });

  const options = useMemo(
    () =>
      (listData?.list ?? [])
        .filter((item): item is ConfigItem & { id: number } => item.id != null)
        .map(item => ({ label: item.name || `${title}${item.id}`, value: item.id })),
    [listData?.list, title]
  );

  useEffect(() => {
    if (options.length === 0) {
      setSelectedId(undefined);
      return;
    }
    if (!selectedId || !options.some(option => option.value === selectedId)) {
      setSelectedId(options[0].value);
    }
  }, [options, selectedId]);

  const { data: detailData, loading: detailLoading } = useRequest(() => detailRequest(selectedId!), {
    ready: selectedId != null,
    refreshDeps: [selectedId, detailRequest],
  });

  const currentCamera = detailData?.[imageType];
  const previewItem = useMemo<RecorderPreviewItem | undefined>(() => {
    if (!currentCamera) return undefined;

    return {
      ...currentCamera,
      deviceId: currentCamera.deviceId ?? currentCamera.deviceSerial,
      deviceName: currentCamera.deviceName ?? currentCamera.name,
      videoPlayUrl: currentCamera.videoPlayUrl ?? currentCamera.liveUrlTranscode ?? currentCamera.supervisoryUrl,
    };
  }, [currentCamera]);

  const content = (
    <div className={classNames(styles.robotPanel, isLargeMapLayout ? styles.robotPanelLarge : '')}>
      <div className={styles.robotInfo}>
        <img src={icon} alt={iconAlt} className={styles.robotIcon} />
        <div className={styles.robotRight}>
          <Select
            className={styles.robotSelect}
            value={selectedId}
            options={options}
            placeholder="请选择"
            getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
            onChange={value => {
              setSelectedId(value as number);
            }}
          />
          <div className={styles.imgBtnGroup}>
            <div
              className={classNames(styles.imgBtn, imageType === RobotImageType.INFRARED ? styles.active : '')}
              onClick={() => setImageType(RobotImageType.INFRARED)}
            >
              {getCameraLabel(RobotImageType.INFRARED)}
            </div>
            <div
              className={classNames(styles.imgBtn, imageType === RobotImageType.VISIBLE_LIGHT ? styles.active : '')}
              onClick={() => setImageType(RobotImageType.VISIBLE_LIGHT)}
            >
              {getCameraLabel(RobotImageType.VISIBLE_LIGHT)}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.videoArea}>
        <Spin spinning={listLoading || detailLoading} wrapperClassName={styles.videoSpin}>
          {previewItem ? (
            <RecorderPreview
              key={`${selectedId}-${imageType}-${previewItem.id ?? previewItem.deviceId ?? previewItem.videoPlayUrl}`}
              item={previewItem}
              hkDomPrefix={`energy_overview_${title === '四足机器人' ? 'dog' : 'robot'}`}
              videoObjectFit="contain"
              videoBackgroundColor="#000"
            />
          ) : (
            <div className={styles.emptyArea}>
              {options.length === 0 ? (
                <>
                  <img src={buildingImg} alt="" />
                  <div>{title}模块开发中</div>
                </>
              ) : (
                <Empty description={`暂无${getCameraLabel(imageType)}监控地址`} />
              )}
            </div>
          )}
        </Spin>
      </div>
    </div>
  );

  if (isLargeMapLayout) return content;

  return (
    <SubTitle title={title} img={titlePng} style={{ flex: titleFlex, paddingTop: 8 }}>
      {content}
    </SubTitle>
  );
};

export default memo(InspectionRobotPanel);
